import { useMemo, useState } from 'react';
import { FilterCard, SectionCard } from '@/components/DataList';
import { ListTable, ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  FlashMessage,
  Form,
  FormActions,
  Input,
  Loading,
  ProSearch,
  Select,
  StatusBadge,
  Textarea,
  VisibilitySelect,
} from '@/components/ui';
import { str } from '@/utils/format';

const WALLET_LABELS: Record<string, string> = {
  recharge: 'محفظة الوحدات SYP',
  games: 'محفظة الألعاب USD',
};

type MethodForm = {
  id: number;
  name: string;
  slug: string;
  account_label: string;
  account_value: string;
  instructions: string;
  wallet_key: string;
  min_amount: string;
  sort_order: number;
  requires_proof: number;
  is_active: number;
};

const EMPTY_FORM: MethodForm = {
  id: 0,
  name: '',
  slug: '',
  account_label: '',
  account_value: '',
  instructions: '',
  wallet_key: 'recharge',
  min_amount: '',
  sort_order: 0,
  requires_proof: 1,
  is_active: 1,
};

function formFromMethod(m: JsonMap): MethodForm {
  return {
    id: Number(m.id) || 0,
    name: str(m.name),
    slug: str(m.slug, ''),
    account_label: str(m.account_label),
    account_value: str(m.account_value),
    instructions: str(m.instructions ?? m.details),
    wallet_key: str(m.wallet_key, 'recharge') === 'games' ? 'games' : 'recharge',
    min_amount: str(m.min_amount, ''),
    sort_order: Number(m.sort_order ?? 0),
    requires_proof: Number(m.requires_proof ?? 1) === 1 ? 1 : 0,
    is_active: Number(m.is_active ?? 1) === 1 ? 1 : 0,
  };
}

export function PaymentsScreen() {
  const state = useAsync(
    () => admin.paymentMethods(),
    (p) => pickList(p, 'data', 'methods', 'items'),
  );

  const [q, setQ] = useState('');
  const [walletFilter, setWalletFilter] = useState('all');
  const [form, setForm] = useState<MethodForm>({ ...EMPTY_FORM });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const list = state.data ?? [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list.filter((m) => {
      if (walletFilter !== 'all' && str(m.wallet_key, 'recharge') !== walletFilter) return false;
      if (!needle) return true;
      const hay = [str(m.name), str(m.account_label), str(m.account_value), str(m.slug)]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [list, q, walletFilter]);

  function resetForm() {
    setForm({ ...EMPTY_FORM });
    setMsg('');
  }

  async function save() {
    if (!form.name.trim()) {
      setMsg('أدخل اسم طريقة الدفع.');
      return;
    }
    setBusy(true);
    const body: JsonMap = {
      ...form,
      details: form.instructions,
      min_amount: Number(form.min_amount) || 0,
    };
    if (!form.id) delete body.id;
    const res = await admin.savePaymentMethod(body);
    setBusy(false);
    setMsg(errorMessage(res, res.success ? 'تم حفظ الطريقة.' : 'تعذر الحفظ.'));
    if (res.success) {
      resetForm();
      state.reload();
    }
  }

  async function disable(id: unknown) {
    if (!window.confirm('تعطيل طريقة الدفع؟')) return;
    const res = await admin.deletePaymentMethod({ id });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="wallet"
      title="طرق الدفع"
      subtitle="إدارة طرق شحن محفظة الوحدات بالليرة ومحفظة الألعاب بالدولار."
    >
      <FlashMessage message={msg} />

      <SectionCard
        title={form.id ? 'تعديل طريقة دفع' : 'إضافة طريقة دفع'}
        icon={<Icon name="edit" size={24} />}
      >
        <Form className="form-grid two-columns" onSubmit={() => void save()}>
          <Input
            label="الاسم"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <Select
            label="المحفظة"
            value={form.wallet_key}
            onChange={(v) => setForm({ ...form, wallet_key: v })}
            options={[
              { value: 'recharge', label: 'محفظة الوحدات SYP' },
              { value: 'games', label: 'محفظة الألعاب USD' },
            ]}
          />
          <Input
            label="عنوان الحساب"
            value={form.account_label}
            onChange={(e) => setForm({ ...form, account_label: e.target.value })}
            placeholder="مثال: رقم سيريتل كاش"
          />
          <Input
            label="قيمة الحساب"
            value={form.account_value}
            onChange={(e) => setForm({ ...form, account_value: e.target.value })}
          />
          <Input
            label="الحد الأدنى"
            value={form.min_amount}
            onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
            inputMode="decimal"
          />
          <Input
            label="الترتيب"
            type="number"
            value={String(form.sort_order)}
            onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
          />
          <VisibilitySelect
            value={form.is_active}
            onChange={(v) => setForm({ ...form, is_active: Number(v) ? 1 : 0 })}
          />
          <Select
            label="إثبات الدفع"
            value={String(form.requires_proof)}
            onChange={(v) => setForm({ ...form, requires_proof: Number(v) ? 1 : 0 })}
            options={[
              { value: '1', label: 'مطلوب' },
              { value: '0', label: 'غير مطلوب' },
            ]}
          />
          <Textarea
            className="span-2"
            label="تعليمات الدفع"
            value={form.instructions}
            onChange={(v) => setForm({ ...form, instructions: v })}
          />
          <FormActions>
            <Button type="submit" disabled={busy}>
              <Icon name="check" size={18} /> حفظ الطريقة
            </Button>
            <Button type="button" variant="secondary" onClick={resetForm}>
              تفريغ
            </Button>
          </FormActions>
        </Form>
      </SectionCard>

      <FilterCard title="الفلاتر" icon={<Icon name="search" size={24} />}>
        <ProSearch value={q} onChange={setQ} placeholder="بحث بطريقة الدفع" />
        <Select
          label="المحفظة"
          value={walletFilter}
          onChange={setWalletFilter}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'recharge', label: 'الوحدات' },
            { value: 'games', label: 'الألعاب' },
          ]}
        />
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> تحديث
        </Button>
      </FilterCard>

      <SectionCard title="طرق الدفع الحالية" icon={<Icon name="wallet" size={24} />}>
        {filtered.length === 0 ? (
          <Empty title="لا توجد طرق دفع" hint="أضف طريقة تظهر عند شحن المحفظة." />
        ) : (
          <ListTable tableClassName="pro-table">
            <thead>
              <tr>
                <th>الطريقة</th>
                <th>المحفظة</th>
                <th>الحساب</th>
                <th>حد أدنى</th>
                <th>الحالة</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={str(m.id)}>
                  <td>
                    <strong>{str(m.name)}</strong>
                    {m.slug ? <small className="muted block">{str(m.slug)}</small> : null}
                  </td>
                  <td>{WALLET_LABELS[str(m.wallet_key, 'recharge')] ?? str(m.wallet_key)}</td>
                  <td>
                    <div className="cell-stack">
                      <span>{str(m.account_label, '—')}</span>
                      <small dir="ltr">{str(m.account_value, '—')}</small>
                    </div>
                  </td>
                  <td>{str(m.min_amount ?? '0')}</td>
                  <td>
                    <StatusBadge status={Number(m.is_active ?? 1) === 1 ? 'active' : 'blocked'} />
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="تعديل"
                        onClick={() => {
                          setForm(formFromMethod(m));
                          setMsg('');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        <Icon name="edit" size={16} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn danger"
                        title="تعطيل"
                        onClick={() => void disable(m.id)}
                      >
                        <Icon name="close" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ListTable>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}
